import { useCallback, useEffect, useState } from 'react';
import { API, normalizeMediaUrl } from '../utils/apiBase';

/**
 * Live storefront taxonomy. Fetches the admin-edited Main → Sub map +
 * style axis. Falls back to a sane default so the nav still renders on
 * first paint / network failure.
 *
 * Source of truth (DB): settings._id = "collections_taxonomy"
 * Edited via Admin → Settings → Collections.
 *
 * Closed-system rule: refetches automatically when the tab regains focus
 * or becomes visible, so changes made in /admin (often in another tab)
 * land on the storefront the moment the user switches back. Also listens
 * for a custom `taxonomy:invalidate` window event for in-app invalidation.
 */
const FALLBACK = {
  main: ['Bedding', 'Living', 'Dining', 'Decor'],
  sub: {
    Bedding: ['Beds', 'Bedside Tables'],
    Living:  ['Sofas', 'Accent Chairs', 'Coffee Tables', 'Shelves', 'Console Tables', 'Side Tables'],
    Dining:  ['Dining Tables', 'Dining Chairs'],
    Decor:   ['Lighting', 'Mirrors', 'Trays', 'Candle Holders', 'Clocks', 'Vases'],
  },
  style: ['Thread', 'Timber'],
};

export const slugify = (v = '') =>
  String(v).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '');

/**
 * Shared meta key for a sub-collection. A sub-collection (e.g. "Lighting") can
 * live under several main collections, but it's ONE category — so its cover
 * image + description are stored under a single `sub/{slug}` key, shared across
 * every room it appears in. Edit it once in Admin, it updates everywhere.
 */
export const subMetaKey = (subName = '') => `sub/${slugify(subName)}`;

/**
 * Resolve the cover/description meta for a sub-collection, preferring whichever
 * record actually HAS a cover. A sub can have meta under the shared `sub/{slug}`
 * key (new) or a legacy `{main}/{sub}` key (older). Empty shared records (e.g.
 * created by an admin "Save all" with no cover) must NOT shadow a populated
 * legacy record — so a record with a real cover always wins.
 */
export const resolveSubMeta = (metaMap = {}, subName = '', mainSlug = '') => {
  const shared = metaMap[subMetaKey(subName)];
  const legacy = mainSlug ? metaMap[`${slugify(mainSlug)}/${slugify(subName)}`] : null;
  if (shared && shared.cover_image_url) return shared;
  if (legacy && legacy.cover_image_url) return legacy;
  return shared || legacy || {};
};

/** Refetch on: mount, window focus, visibility change, and custom invalidation event. */
const useLiveFetch = (url, parse, initial, eventName) => {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    let alive = true;
    fetch(`${url}${url.includes('?') ? '&' : '?'}_=${Date.now()}`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { if (alive) { setData(parse(d)); setLoading(false); } })
      .catch(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [url, parse]);

  useEffect(() => {
    const cancel = load();
    const onFocus = () => load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    if (eventName) window.addEventListener(eventName, onFocus);
    return () => {
      cancel && cancel();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      if (eventName) window.removeEventListener(eventName, onFocus);
    };
  }, [load, eventName]);

  return { data, loading };
};

export const useTaxonomy = () => {
  const { data, loading } = useLiveFetch(
    `${API}/api/collections-taxonomy`,
    (d) => ({
      main: Array.isArray(d?.main) && d.main.length ? d.main : FALLBACK.main,
      sub:  d?.sub && typeof d.sub === 'object' ? d.sub : FALLBACK.sub,
      style: Array.isArray(d?.style) && d.style.length ? d.style : FALLBACK.style,
    }),
    FALLBACK,
    'taxonomy:invalidate',
  );
  return { ...data, loading };
};

/**
 * Live per-collection metadata (cover image, description, custom title)
 * keyed by slug. Used by /collections landing + sub-collection drill-downs.
 * Keyed by slug e.g. "bedding", "living/sofas", "decor/lighting".
 */
export const useCollectionMeta = () => {
  const { data } = useLiveFetch(
    `${API}/api/collection-meta`,
    (d) => (d?.items || []).map((it) => ({ ...it, cover_image_url: normalizeMediaUrl(it.cover_image_url) })),
    [],
    'collection-meta:invalidate',
  );
  const items = data;
  const map = items.reduce((acc, it) => ({ ...acc, [it.slug]: it }), {});
  return { items, map };
};
